import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { GTMEventLogger } from 'utils/analytics';

import { GTMEvents } from 'constants/enums';
import {
  COPIED_TO_CLIPBOARD_NOTIFICATION_DURATION_MS,
  COPIED_TO_CLIPBOARD_NOTIFICATION_TEXT
} from 'constants/uiSettings';

import { ReactComponent as Phone } from 'assets/icons/call.svg';
import { ReactComponent as Copy } from 'assets/icons/document-copy.svg';
import { ReactComponent as Email } from 'assets/icons/email.svg';

import styles from './Contacts.module.scss';

interface ContactsProps {
  email?: string | null;
  phone?: string | null;
}

interface ContactItemProps {
  icon: FC<{ className?: string }>;
  value: string;
  label: string;
  event: GTMEvents;
}

const ContactItem: FC<ContactItemProps> = ({
  icon: Icon,
  value,
  label,
  event
}) => {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(value);
    setCopied(true);

    GTMEventLogger(event);

    setTimeout(
      () => setCopied(false),
      COPIED_TO_CLIPBOARD_NOTIFICATION_DURATION_MS
    );
  };

  return (
    <div className={styles.contact}>
      <div className={styles.contactContent}>
        <div className={styles.contactInfo}>
          <Icon className={styles.contactIcon} />
          {value}
        </div>
        <button
          className={`${styles.copy} ${copied ? styles.copied : ''}`}
          onClick={handleCopy}
        >
          {copied ? (
            t(COPIED_TO_CLIPBOARD_NOTIFICATION_TEXT)
          ) : (
            <Copy className={styles.copyIcon} />
          )}
        </button>
      </div>
      <div className={styles.contactLabel}>{label}</div>
    </div>
  );
};

const Contacts: FC<ContactsProps> = ({ email, phone }: ContactsProps) => {
  const { t } = useTranslation();

  return (
    <div className={styles.wrap}>
      {email && (
        <ContactItem
          icon={Email}
          value={email}
          label={t('common.label.email')}
          event={GTMEvents.ApplicationsListViewProfileCopyEmail}
        />
      )}
      {phone && (
        <ContactItem
          icon={Phone}
          value={phone}
          label={t('common.label.phoneNumber')}
          event={GTMEvents.ApplicationsListViewProfileCopyPhone}
        />
      )}
    </div>
  );
};

export default Contacts;
